// Repeating a week you have already planned.
//
// "Repeat week" takes the week on screen and does it again, N weeks running:
// the desks you hold and the days you said where you were, because a week where
// you told everybody you were at home on Wednesday is not repeated faithfully
// if Wednesday comes back blank.
//
// The expansion is here rather than in the page so the rules that matter — a
// week is seven days on, nothing lands in the past, one request per desk and
// slot — can be exercised without a browser.

// Extension included so node --test can import this file directly; Vite is
// happy either way.
import { addDays, parseDate } from './bookingsTheme.js';

const dayMs = 86400000;

export const daysBetween = (a, b) => Math.round((parseDate(b) - parseDate(a)) / dayMs);

// How many whole weeks fit between the end of the week being repeated and the
// end of the booking window. Whole weeks, because that is the unit the button
// repeats: a week that would land half inside the window is not offered at all
// rather than quietly booked in half.
export const weeksAvailable = (lastDate, maxDate) =>
  Math.max(0, Math.floor(daysBetween(lastDate, maxDate) / 7));

// Why a date in the repeat could not be booked, as a sentence.
//
// Repeating the same week twice is an easy thing to do by accident, and the
// server answers that with an ordinary "this desk is taken" naming the holder —
// who, in that case, is you. Reported straight through it reads as a stranger
// with your name having taken your seat, so a seat already yours is said to be
// yours.
export const skipReason = (conflict, { label, mine } = {}) => {
  if (mine) return `you already have ${label}`;
  if (conflict?.own_desk) return `${label} not needed, you already have ${conflict.own_desk}`;
  return `${label} is taken by ${conflict?.person_name || 'someone else'}`;
};

// plan: [{ date, seats: [{ resource_id, slot }], stated: { status_code, location_id } | null }]
//
// Returns the requests to make: one per desk and slot, and one per statement,
// each carrying every date it applies to. Grouped rather than sent per day
// because POST /bookings takes a list of dates and reports on each of them, so
// a fortnight of Tuesdays is one call and one answer instead of eight.
export const expandWeek = (plan, weeks, { after } = {}) => {
  const desks = new Map();
  const statements = new Map();
  const collect = (map, key, seed, date) => {
    if (!map.has(key)) map.set(key, { ...seed, dates: [] });
    map.get(key).dates.push(date);
  };

  for (let week = 1; week <= weeks; week++) {
    for (const day of plan || []) {
      const date = addDays(day.date, 7 * week);
      // Looking back at a week that has been, part of its repeat may still be
      // behind us. Booking backwards is never what was meant.
      if (after && date <= after) continue;
      for (const seat of day.seats || []) {
        collect(desks, `${seat.resource_id}|${seat.slot}`,
          { resource_id: seat.resource_id, slot: seat.slot }, date);
      }
      if (day.stated) {
        // The slot is part of the statement's identity: a half-day Wednesday at
        // home repeats as a half day, not silently as a whole one.
        collect(statements, `${day.stated.status_code}|${day.stated.location_id || ''}|${day.stated.slot || 'full'}`,
          { status_code: day.stated.status_code, location_id: day.stated.location_id || null, slot: day.stated.slot || 'full' }, date);
      }
    }
  }

  const sorted = (map) => [...map.values()].map(x => ({ ...x, dates: [...x.dates].sort() }));
  return { desks: sorted(desks), statements: sorted(statements) };
};
